import {
  isSupportedAppointmentWorkingDraftTransition,
} from "@/app/booking/adapters/specialistAppointmentCompletionContext";
import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  appointmentTerminalPresentationCoversDraft,
  validateAppointmentTerminalPresentationSnapshot,
  type AppointmentTerminalPresentationSnapshotV1,
} from "@/app/booking/core/appointmentTerminalPresentation";
import { isAppointmentTimeProfessionalMutationEvidenceCurrent } from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";

import {
  createAppointmentWaitlistDraftIdempotencyKey,
  createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration,
  validateAppointmentWaitlistDraft,
  validateRetiredGroupAppointmentWaitlistDraftForStorageMigration,
  type AppointmentWaitlistDraftV3,
} from "./appointmentWaitlistDraft";
import {
  isAppointmentWaitlistConfigurationPublicationCurrent,
  isAppointmentWaitlistConflictPublicationCurrent,
  isRetiredGroupAppointmentWaitlistConfigurationPublicationCurrentForStorageMigration,
  isRetiredGroupAppointmentWaitlistConflictPublicationCurrentForStorageMigration,
} from "./appointmentWaitlistConfiguration";
import {
  validateAppointmentWaitlistProviderResult,
  validateRetiredGroupAppointmentWaitlistProviderResultForStorageMigration,
  type AppointmentWaitlistSubmission,
} from "./appointmentWaitlistProvider";

export const APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_SCHEMA_VERSION = 1 as const;

export type AppointmentWaitlistSubmissionNotAppliedReason =
  | "invalid-customer"
  | "invalid-draft"
  | "stale-draft"
  | "mutation-not-invoked";

export type AppointmentWaitlistSubmissionAttemptStateV1 =
  | { kind: "pending-reconciliation" }
  | { kind: "accepted"; submission: AppointmentWaitlistSubmission }
  | {
      kind: "not-applied";
      reason: AppointmentWaitlistSubmissionNotAppliedReason;
      retryable: boolean;
      confirmedAtEpochMs: number;
    };

export interface AppointmentWaitlistSubmissionAttemptV1 {
  schemaVersion: typeof APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_SCHEMA_VERSION;
  kind: "appointment-waitlist-submission-attempt";
  draftId: string;
  customerAccountId: string;
  activatedAtEpochMs: number;
  sourceAppointmentDraft: AppointmentBookingDraftV1;
  presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
  waitlistDraft: AppointmentWaitlistDraftV3;
  idempotencyKey: string;
  state: AppointmentWaitlistSubmissionAttemptStateV1;
}

export interface AppointmentWaitlistReconciliationProvider {
  reconcileWaitlistRequest(
    attempt: AppointmentWaitlistSubmissionAttemptV1,
    options?: { signal?: AbortSignal },
  ): Promise<
    | { status: "accepted"; submission: AppointmentWaitlistSubmission }
    | {
        status: "not-applied";
        reason: AppointmentWaitlistSubmissionNotAppliedReason;
        retryable: boolean;
        confirmedAtEpochMs: number;
      }
    | { status: "pending" }
  >;
}

export type AppointmentWaitlistSubmissionAttemptValidation =
  | { valid: true; attempt: AppointmentWaitlistSubmissionAttemptV1 }
  | { valid: false; reason: "invalid-appointment-waitlist-submission-attempt" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isEpoch(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0
  );
}

function cloneState(input: {
  value: unknown;
  draft: AppointmentWaitlistDraftV3;
  idempotencyKey: string;
  activatedAtEpochMs: number;
  evaluatedAtEpochMs: number;
  expectedMode: AppointmentBookingDraftV1["mode"];
}): AppointmentWaitlistSubmissionAttemptStateV1 | null {
  const { value } = input;
  if (!isRecord(value)) return null;
  if (value.kind === "pending-reconciliation") {
    return { kind: "pending-reconciliation" };
  }
  if (value.kind === "accepted") {
    const resultInput = {
      draft: input.draft,
      idempotencyKey: input.idempotencyKey,
      result: { ok: true, submission: value.submission },
      acceptedNotBeforeEpochMs: input.activatedAtEpochMs,
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
    };
    const result =
      input.expectedMode === "individual"
        ? validateAppointmentWaitlistProviderResult(resultInput)
        : validateRetiredGroupAppointmentWaitlistProviderResultForStorageMigration(
            resultInput,
          );
    return result.ok
      ? { kind: "accepted", submission: result.submission }
      : null;
  }
  const reasons = new Set<AppointmentWaitlistSubmissionNotAppliedReason>([
    "invalid-customer",
    "invalid-draft",
    "stale-draft",
    "mutation-not-invoked",
  ]);
  if (
    value.kind === "not-applied" &&
    typeof value.reason === "string" &&
    reasons.has(value.reason as AppointmentWaitlistSubmissionNotAppliedReason) &&
    typeof value.retryable === "boolean" &&
    value.retryable === (value.reason === "mutation-not-invoked") &&
    isEpoch(value.confirmedAtEpochMs) &&
    value.confirmedAtEpochMs >= input.activatedAtEpochMs &&
    value.confirmedAtEpochMs <= input.evaluatedAtEpochMs + 5 * 60_000
  ) {
    return {
      kind: "not-applied",
      reason: value.reason as AppointmentWaitlistSubmissionNotAppliedReason,
      retryable: value.retryable,
      confirmedAtEpochMs: value.confirmedAtEpochMs,
    };
  }
  return null;
}

/**
 * Durable owner-bound ledger for one invoked Waitlist mutation. The immutable
 * request tuple can change only through the explicit state transitions below.
 */
function validateAppointmentWaitlistSubmissionAttemptForMode(
  value: unknown,
  expectedMode: AppointmentBookingDraftV1["mode"],
  options: { evaluatedAtEpochMs?: number } = {},
): AppointmentWaitlistSubmissionAttemptValidation {
  const evaluatedAtEpochMs = options.evaluatedAtEpochMs ?? Date.now();
  if (
    !isRecord(value) ||
    value.schemaVersion !== APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_SCHEMA_VERSION ||
    value.kind !== "appointment-waitlist-submission-attempt" ||
    typeof value.draftId !== "string" ||
    value.draftId.length === 0 ||
    value.draftId.length > 160 ||
    typeof value.customerAccountId !== "string" ||
    value.customerAccountId.length === 0 ||
    value.customerAccountId.length > 160 ||
    !isEpoch(value.activatedAtEpochMs) ||
    !isEpoch(evaluatedAtEpochMs) ||
    value.activatedAtEpochMs > evaluatedAtEpochMs + 5 * 60_000 ||
    typeof value.idempotencyKey !== "string" ||
    value.idempotencyKey.length === 0 ||
    value.idempotencyKey.length > 320
  ) {
    return { valid: false, reason: "invalid-appointment-waitlist-submission-attempt" };
  }
  const source = validateAppointmentStepOneBookingDraft(value.sourceAppointmentDraft);
  const draft =
    expectedMode === "individual"
      ? validateAppointmentWaitlistDraft(value.waitlistDraft)
      : validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(
          value.waitlistDraft,
        );
  const presentation = validateAppointmentTerminalPresentationSnapshot(
    value.presentationSnapshot,
  );
  if (
    !source.valid ||
    !draft.valid ||
    source.draft.mode !== expectedMode ||
    !presentation ||
    draft.draft.draftId !== value.draftId ||
    draft.draft.customerAccountId !== value.customerAccountId ||
    draft.draft.stage !== "confirmation" ||
    draft.draft.confirmation === null ||
    source.draft.draftId !== draft.draft.appointmentDraft.draftId ||
    !isSupportedAppointmentWorkingDraftTransition({
      sourceDraft: source.draft,
      workingDraft: draft.draft.appointmentDraft,
      staffChoiceEvidence: draft.draft.staffChoiceEvidence,
      timeProfessionalMutationEvidence:
        draft.draft.timeProfessionalMutationEvidence,
      evaluatedAtEpochMs: value.activatedAtEpochMs,
    }) ||
    !(expectedMode === "individual"
      ? isAppointmentWaitlistConfigurationPublicationCurrent({
          publication: draft.draft.configurationPublication,
          expectedProviderIdentity:
            draft.draft.configurationPublication.providerIdentity,
          evaluatedAtEpochMs: value.activatedAtEpochMs,
        })
      : isRetiredGroupAppointmentWaitlistConfigurationPublicationCurrentForStorageMigration(
          {
            publication: draft.draft.configurationPublication,
            expectedProviderIdentity:
              draft.draft.configurationPublication.providerIdentity,
            evaluatedAtEpochMs: value.activatedAtEpochMs,
          },
        )) ||
    draft.draft.conflictPublication?.result.kind !== "none" ||
    !(expectedMode === "individual"
      ? isAppointmentWaitlistConflictPublicationCurrent({
          publication: draft.draft.conflictPublication,
          configuration: draft.draft.configurationPublication,
          preferences: draft.draft.preferences,
          expectedProviderIdentity:
            draft.draft.configurationPublication.providerIdentity,
          evaluatedAtEpochMs: value.activatedAtEpochMs,
        })
      : isRetiredGroupAppointmentWaitlistConflictPublicationCurrentForStorageMigration(
          {
            publication: draft.draft.conflictPublication,
            configuration: draft.draft.configurationPublication,
            preferences: draft.draft.preferences,
            expectedProviderIdentity:
              draft.draft.configurationPublication.providerIdentity,
            evaluatedAtEpochMs: value.activatedAtEpochMs,
          },
        )) ||
    (draft.draft.timeProfessionalMutationEvidence !== null &&
      !isAppointmentTimeProfessionalMutationEvidenceCurrent({
        evidence: draft.draft.timeProfessionalMutationEvidence,
        expectedProviderIdentity: {
          providerId: draft.draft.timeProfessionalMutationEvidence.providerId,
          evidenceVersion:
            draft.draft.timeProfessionalMutationEvidence.evidenceVersion,
        },
        evaluatedAtEpochMs: value.activatedAtEpochMs,
      })) ||
    !appointmentTerminalPresentationCoversDraft({
      draft: draft.draft.appointmentDraft,
      presentationSnapshot: presentation,
    }) ||
    (expectedMode === "individual"
      ? createAppointmentWaitlistDraftIdempotencyKey(draft.draft)
      : createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration(
          draft.draft,
        )) !== value.idempotencyKey
  ) {
    return { valid: false, reason: "invalid-appointment-waitlist-submission-attempt" };
  }
  const state = cloneState({
    value: value.state,
    draft: draft.draft,
    idempotencyKey: value.idempotencyKey,
    activatedAtEpochMs: value.activatedAtEpochMs,
    evaluatedAtEpochMs,
    expectedMode,
  });
  if (!state) {
    return { valid: false, reason: "invalid-appointment-waitlist-submission-attempt" };
  }
  return {
    valid: true,
    attempt: {
      schemaVersion: APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_SCHEMA_VERSION,
      kind: "appointment-waitlist-submission-attempt",
      draftId: draft.draft.draftId,
      customerAccountId: value.customerAccountId,
      activatedAtEpochMs: value.activatedAtEpochMs,
      sourceAppointmentDraft: source.draft,
      presentationSnapshot: presentation,
      waitlistDraft: draft.draft,
      idempotencyKey: value.idempotencyKey,
      state,
    },
  };
}

export function validateAppointmentWaitlistSubmissionAttempt(
  value: unknown,
  options: { evaluatedAtEpochMs?: number } = {},
): AppointmentWaitlistSubmissionAttemptValidation {
  return validateAppointmentWaitlistSubmissionAttemptForMode(
    value,
    "individual",
    options,
  );
}

export function validateRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration(
  value: unknown,
  options: { evaluatedAtEpochMs?: number } = {},
): AppointmentWaitlistSubmissionAttemptValidation {
  return validateAppointmentWaitlistSubmissionAttemptForMode(
    value,
    "group",
    options,
  );
}

export function createAppointmentWaitlistSubmissionAttempt(input: {
  activatedAtEpochMs?: number;
  sourceAppointmentDraft: AppointmentBookingDraftV1;
  presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
  waitlistDraft: AppointmentWaitlistDraftV3;
}) {
  const idempotencyKey = createAppointmentWaitlistDraftIdempotencyKey(input.waitlistDraft);
  if (!idempotencyKey) return null;
  const validation = validateAppointmentWaitlistSubmissionAttempt({
    schemaVersion: APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_SCHEMA_VERSION,
    kind: "appointment-waitlist-submission-attempt",
    draftId: input.waitlistDraft.draftId,
    customerAccountId: input.waitlistDraft.customerAccountId,
    activatedAtEpochMs: input.activatedAtEpochMs ?? Date.now(),
    sourceAppointmentDraft: input.sourceAppointmentDraft,
    presentationSnapshot: input.presentationSnapshot,
    waitlistDraft: input.waitlistDraft,
    idempotencyKey,
    state: { kind: "pending-reconciliation" },
  });
  return validation.valid ? validation.attempt : null;
}

export function createRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration(
  input: {
    activatedAtEpochMs?: number;
    sourceAppointmentDraft: AppointmentBookingDraftV1;
    presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
    waitlistDraft: AppointmentWaitlistDraftV3;
  },
) {
  const idempotencyKey =
    createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration(
      input.waitlistDraft,
    );
  if (!idempotencyKey) return null;
  const validation =
    validateRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration(
      {
        schemaVersion: APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_SCHEMA_VERSION,
        kind: "appointment-waitlist-submission-attempt",
        draftId: input.waitlistDraft.draftId,
        customerAccountId: input.waitlistDraft.customerAccountId,
        activatedAtEpochMs: input.activatedAtEpochMs ?? Date.now(),
        sourceAppointmentDraft: input.sourceAppointmentDraft,
        presentationSnapshot: input.presentationSnapshot,
        waitlistDraft: input.waitlistDraft,
        idempotencyKey,
        state: { kind: "pending-reconciliation" },
      },
      { evaluatedAtEpochMs: input.activatedAtEpochMs ?? Date.now() },
    );
  return validation.valid ? validation.attempt : null;
}

export function markAppointmentWaitlistSubmissionAttemptAccepted(input: {
  attempt: unknown;
  submission: AppointmentWaitlistSubmission;
  evaluatedAtEpochMs?: number;
}) {
  const evaluatedAtEpochMs = input.evaluatedAtEpochMs ?? Date.now();
  const validation = validateAppointmentWaitlistSubmissionAttempt(
    input.attempt,
    { evaluatedAtEpochMs },
  );
  if (!validation.valid || validation.attempt.state.kind !== "pending-reconciliation") {
    return null;
  }
  const next = validateAppointmentWaitlistSubmissionAttempt(
    {
      ...validation.attempt,
      state: { kind: "accepted", submission: input.submission },
    },
    { evaluatedAtEpochMs },
  );
  return next.valid ? next.attempt : null;
}

export function markAppointmentWaitlistSubmissionAttemptNotApplied(input: {
  attempt: unknown;
  reason: AppointmentWaitlistSubmissionNotAppliedReason;
  retryable: boolean;
  confirmedAtEpochMs?: number;
  evaluatedAtEpochMs?: number;
}) {
  const evaluatedAtEpochMs = input.evaluatedAtEpochMs ?? Date.now();
  const confirmedAtEpochMs = input.confirmedAtEpochMs ?? evaluatedAtEpochMs;
  const validation = validateAppointmentWaitlistSubmissionAttempt(
    input.attempt,
    { evaluatedAtEpochMs },
  );
  if (!validation.valid || validation.attempt.state.kind !== "pending-reconciliation") {
    return null;
  }
  const next = validateAppointmentWaitlistSubmissionAttempt(
    {
      ...validation.attempt,
      state: {
        kind: "not-applied",
        reason: input.reason,
        retryable: input.retryable,
        confirmedAtEpochMs,
      },
    },
    { evaluatedAtEpochMs },
  );
  return next.valid ? next.attempt : null;
}

export function retryAppointmentWaitlistSubmissionAttempt(input: {
  attempt: unknown;
  evaluatedAtEpochMs?: number;
}) {
  const evaluatedAtEpochMs = input.evaluatedAtEpochMs ?? Date.now();
  const validation = validateAppointmentWaitlistSubmissionAttempt(
    input.attempt,
    { evaluatedAtEpochMs },
  );
  if (
    !validation.valid ||
    validation.attempt.state.kind !== "not-applied" ||
    validation.attempt.state.reason !== "mutation-not-invoked" ||
    !validation.attempt.state.retryable
  ) {
    return null;
  }
  const next = validateAppointmentWaitlistSubmissionAttempt(
    {
      ...validation.attempt,
      state: { kind: "pending-reconciliation" },
    },
    { evaluatedAtEpochMs },
  );
  return next.valid ? next.attempt : null;
}

/**
 * Validates an untrusted reconciliation response. Anything except an exact
 * accepted/not-applied proof leaves the durable attempt pending.
 */
export function applyAppointmentWaitlistReconciliationResult(input: {
  attempt: unknown;
  result: unknown;
  evaluatedAtEpochMs?: number;
}) {
  const evaluatedAtEpochMs = input.evaluatedAtEpochMs ?? Date.now();
  const validation = validateAppointmentWaitlistSubmissionAttempt(
    input.attempt,
    { evaluatedAtEpochMs },
  );
  if (
    !validation.valid ||
    validation.attempt.state.kind !== "pending-reconciliation" ||
    !isRecord(input.result)
  ) {
    return validation.valid ? validation.attempt : null;
  }
  if (input.result.status === "accepted") {
    return (
      markAppointmentWaitlistSubmissionAttemptAccepted({
        attempt: validation.attempt,
        submission: input.result.submission as AppointmentWaitlistSubmission,
        evaluatedAtEpochMs,
      }) ?? validation.attempt
    );
  }
  if (input.result.status === "not-applied") {
    return (
      markAppointmentWaitlistSubmissionAttemptNotApplied({
        attempt: validation.attempt,
        reason:
          input.result.reason as AppointmentWaitlistSubmissionNotAppliedReason,
        retryable: input.result.retryable as boolean,
        confirmedAtEpochMs: input.result.confirmedAtEpochMs as number,
        evaluatedAtEpochMs,
      }) ?? validation.attempt
    );
  }
  return validation.attempt;
}
